import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { Maquina, TrabajoProgramado } from '@laser/core';
import { useCatalogo, usePrograma, useTrabajos } from '../api/hooks';
import type { TrabajoResumen } from '../api/tipos';
import { Cargando, Chip, ErrorCaja, Insignia } from '../componentes/ui';
import { fechaCorta, fechaHora, hora, inicioDelDia, partesEnZona, sumarDias } from '../lib/fechas';
import { duracion, porcentaje } from '../lib/formato';
import { intervalosLaborales } from '../lib/horario';
import { holguraTexto, minutosEstimados } from '../lib/trabajo';
import { useAhora } from '../lib/useAhora';

const DIAS = 14;
const ZOOM = { dia: 48, semana: 14, quincena: 6 } as const;
type Zoom = keyof typeof ZOOM;
const H = 3600_000;

export default function Programa() {
  const programa = usePrograma();
  const cat = useCatalogo();
  const trabajosQ = useTrabajos({});
  const ahora = useAhora(60_000);
  const [zoom, setZoom] = useState<Zoom>('dia');
  const scrollRef = useRef<HTMLDivElement>(null);

  const trabajos = useMemo(() => new Map<string, TrabajoResumen>((trabajosQ.data?.pages ?? []).flatMap((p) => p.trabajos).map((t) => [t.id, t])), [trabajosQ.data]);
  const inicio = inicioDelDia(ahora);
  const fin = sumarDias(inicio, DIAS);
  const pxH = ZOOM[zoom];
  const x = (t: number) => ((t - inicio) / H) * pxH;
  const anchoLienzo = x(fin);

  const maquinas = useMemo(() => {
    const conTrabajo = new Set((programa.data?.trabajos ?? []).map((p) => p.maquinaId));
    return cat.maquinas.filter((m) => m.activa || conTrabajo.has(m.id));
  }, [cat.maquinas, programa.data]);

  const porMaquina = useMemo(() => {
    const m = new Map<string, TrabajoProgramado[]>();
    for (const p of programa.data?.trabajos ?? []) m.set(p.maquinaId, [...(m.get(p.maquinaId) ?? []), p]);
    for (const l of m.values()) l.sort((a, b) => a.inicio.localeCompare(b.inicio));
    return m;
  }, [programa.data]);

  const dias = useMemo(() => Array.from({ length: DIAS }, (_, i) => sumarDias(inicio, i)), [inicio]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && programa.data) el.scrollLeft = Math.max(0, x(ahora) - 2 * pxH);
  }, [zoom, programa.data ? 1 : 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (programa.isLoading || cat.cargando) return <Cargando />;
  if (programa.isError) return <ErrorCaja error={programa.error} reintentar={() => programa.refetch()} />;
  const p = programa.data!;
  const tarde = p.trabajos.filter((t) => t.tarde).sort((a, b) => a.holguraMin - b.holguraMin);
  const nombre = (id: string) => trabajos.get(id);

  return (
    <>
      <div className="titulo">
        <h1>Programa</h1>
        <span className="sub">Próximos {DIAS} días · calculado {hora(p.generadoEn)}</span>
        <div className="grow" />
        <button type="button" className="btn btn--sm" onClick={() => programa.refetch()} disabled={programa.isFetching}>{programa.isFetching ? 'Recalculando…' : 'Recalcular'}</button>
      </div>

      <section className="pila" style={{ marginBottom: 18 }} aria-labelledby="ahora-t">
        <h2 id="ahora-t" className="h2">¿Qué corto ahora?</h2>
        <div className="ahora-lista">
          {maquinas.filter((m) => m.activa).map((m) => (
            <QueCortoAhora key={m.id} maquina={m} lista={porMaquina.get(m.id) ?? []} trabajos={trabajos} material={(id) => cat.material.get(id)} ahora={ahora} />
          ))}
        </div>
      </section>

      {(tarde.length > 0 || p.sinMaquina.length > 0) && (
        <div className="rejilla-2" style={{ marginBottom: 18 }}>
          {tarde.length > 0 && (
            <section className="tarjeta" aria-labelledby="tarde-t">
              <h2 id="tarde-t" className="h2" style={{ color: 'var(--bad)' }}>Van tarde ({tarde.length})</h2>
              {tarde.map((t) => {
                const tr = nombre(t.trabajoId);
                return (
                  <div className="linea" key={t.trabajoId}>
                    <span className="celda-2">
                      <Link to={`/trabajos/${t.trabajoId}`}><b>{tr?.folio ?? t.trabajoId}</b></Link>
                      <small>{tr?.cliente?.nombre ?? cat.cliente.get(tr?.clienteId ?? '')?.nombre} · termina {fechaHora(t.fin)} · compromiso {tr ? fechaHora(tr.fechaCompromiso) : '—'}</small>
                    </span>
                    <Insignia tono="bad">{holguraTexto(t.holguraMin)}</Insignia>
                  </div>
                );
              })}
            </section>
          )}
          {p.sinMaquina.length > 0 && (
            <section className="tarjeta" aria-labelledby="sin-t">
              <h2 id="sin-t" className="h2">Sin programar ({p.sinMaquina.length})</h2>
              {p.sinMaquina.map((s) => {
                const tr = nombre(s.trabajoId);
                return (
                  <div className="linea" key={s.trabajoId}>
                    <span className="celda-2">
                      <Link to={`/trabajos/${s.trabajoId}`}><b>{tr?.folio ?? s.trabajoId}</b></Link>
                      <small>{s.motivo}</small>
                    </span>
                    {tr && <Chip estado={tr.estado} />}
                  </div>
                );
              })}
            </section>
          )}
        </div>
      )}

      <div className="fila fila--entre" style={{ marginBottom: 8 }}>
        <h2 className="h2" style={{ margin: 0 }}>Línea de tiempo</h2>
        <div className="seg" role="group" aria-label="Zoom">
          {(['dia', 'semana', 'quincena'] as Zoom[]).map((z) => (
            <button key={z} type="button" aria-pressed={zoom === z} onClick={() => setZoom(z)}>{z === 'dia' ? 'Día' : z === 'semana' ? 'Semana' : '14 días'}</button>
          ))}
        </div>
      </div>

      <div className="gantt-caja">
        <div className="gantt">
          <div className="gantt__nombres">
            <div className="gantt__nombre gantt__nombre--cab"><span className="h3" style={{ margin: 0 }}>Máquina</span></div>
            {maquinas.map((m) => {
              const oc = p.ocupacionPorMaquina[m.id] ?? 0;
              return (
                <div className="gantt__nombre" key={m.id}>
                  <b>{m.nombre}</b>
                  <div className={`ocupacion${oc > 0.85 ? ' ocupacion--alta' : ''}`} title={`Ocupación en ${DIAS} días`}><i style={{ width: `${Math.round(oc * 100)}%` }} /></div>
                  <span className="meta">{porcentaje(oc)} ocupada{m.activa ? '' : ' · inactiva'}</span>
                </div>
              );
            })}
          </div>
          <div className="gantt__scroll" ref={scrollRef}>
            <div className="gantt__lienzo" style={{ width: anchoLienzo }}>
              <div className="gantt__dias">
                {dias.map((d, i) => {
                  const w = x(i + 1 < DIAS ? dias[i + 1] : fin) - x(d);
                  const pz = partesEnZona(d);
                  return (
                    <div key={d} className={`gantt__dia${i === 0 ? ' gantt__dia--hoy' : ''}`} style={{ width: w }}>
                      <b>{i === 0 ? 'Hoy' : fechaCorta(d).split(' ')[0]}</b>{pz.dia}/{pz.mes}
                    </div>
                  );
                })}
              </div>
              {maquinas.map((m) => (
                <div key={m.id} className="gantt__fila" aria-label={`Programa de ${m.nombre}`}>
                  {intervalosLaborales(m.horario, inicio, fin).map(([a, b]) => (
                    <div key={a} className="gantt__turno" style={{ left: x(a), width: x(b) - x(a) }} title={`Turno ${hora(a)}–${hora(b)}`} />
                  ))}
                  {(porMaquina.get(m.id) ?? []).map((pt) => {
                    const tr = trabajos.get(pt.trabajoId);
                    const a = Math.max(Date.parse(pt.inicio), inicio), b = Math.min(Date.parse(pt.fin), fin);
                    if (b <= a) return null;
                    const clases = ['gantt__barra', pt.tarde && 'gantt__barra--tarde', tr?.estado === 'cortando' && 'gantt__barra--cortando', tr?.urgente && 'gantt__barra--urgente'].filter(Boolean).join(' ');
                    return (
                      <Link key={pt.trabajoId} to={`/trabajos/${pt.trabajoId}`} className={clases} style={{ left: x(a), width: Math.max(6, x(b) - x(a)) }}
                        title={`${tr?.folio ?? ''} · ${tr?.cliente?.nombre ?? ''}\n${fechaHora(pt.inicio)} → ${fechaHora(pt.fin)}\n${holguraTexto(pt.holguraMin)}`}>
                        {x(b) - x(a) >= 44 && <>
                          <b>{tr?.folio ?? pt.trabajoId}</b>
                          <span>{pt.tarde ? holguraTexto(pt.holguraMin) : tr?.cliente?.nombre ?? ''}</span>
                        </>}
                      </Link>
                    );
                  })}
                  <div className="gantt__ahora" style={{ left: x(ahora) }} aria-hidden="true" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="meta" style={{ marginTop: 8 }}>Rayado = fuera de horario. Naranja = cortando ahora · rojo = terminará después del compromiso.</p>
    </>
  );
}

function QueCortoAhora({ maquina, lista, trabajos, material, ahora }: {
  maquina: Maquina; lista: TrabajoProgramado[]; trabajos: Map<string, TrabajoResumen>;
  material: (id: string) => { nombre: string; espesorMm: number } | undefined; ahora: number;
}) {
  const siguiente = lista.find((p) => Date.parse(p.fin) > ahora);
  const luego = siguiente ? lista[lista.indexOf(siguiente) + 1] : undefined;
  if (!siguiente) {
    return (
      <div className="ahora-item ahora-item--vacio">
        <b style={{ color: 'var(--text)' }}>{maquina.nombre}</b>
        <span>Sin trabajos en cola.</span>
      </div>
    );
  }
  const t = trabajos.get(siguiente.trabajoId);
  const mat = t ? material(t.solicitud.materialId) : undefined;
  const empezo = Date.parse(siguiente.inicio) <= ahora;
  return (
    <div className="ahora-item">
      <div className="fila fila--entre"><b>{maquina.nombre}</b>{t && <Chip estado={t.estado} />}</div>
      <Link to={`/trabajos/${siguiente.trabajoId}`} className="grande">{t?.folio ?? siguiente.trabajoId} · {t?.cliente?.nombre ?? ''}</Link>
      <span className="muted" style={{ fontSize: '.86rem' }}>
        {mat ? `${mat.nombre} ${mat.espesorMm} mm` : ''}{t ? ` · ~${duracion(minutosEstimados(t) * 60)} de láser` : ''}
      </span>
      <span className="fila" style={{ gap: 6 }}>
        {t?.estado === 'cortando'
          ? <Insignia tono="accent">En la máquina</Insignia>
          : empezo ? <Insignia tono="warn">Ya debería estar cortándose</Insignia> : <span className="meta">Empieza {fechaHora(siguiente.inicio)}</span>}
        {siguiente.tarde && <Insignia tono="bad">{holguraTexto(siguiente.holguraMin)}</Insignia>}
        {t?.urgente && <Insignia tono="accent">Urgente</Insignia>}
      </span>
      {luego && <span className="meta">Después: {trabajos.get(luego.trabajoId)?.folio ?? luego.trabajoId} · {hora(luego.inicio)}</span>}
    </div>
  );
}
